/**
 * Admin Data Hook - Detachable Admin Module
 * 
 * This hook manages data fetching and state for admin operations.
 * When extracting to a separate app, this hook can be moved with minimal changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { adminApi, handleAdminApiError } from '../services/adminApi';
import type {
  PlatformUser,
  UserListParams,
  UserCreditsResponse,
  UserProjectsResponse,
  UserIdentifier
} from '../types';

// ================================
// USER LIST HOOK
// ================================

interface UseUserListReturn {
  users: PlatformUser[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  loadUsers: (params?: UserListParams) => Promise<void>;
  refreshUsers: () => Promise<void>;
  searchUsers: (query: string) => Promise<void>;
}

export const useUserList = (): UseUserListReturn => {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [lastParams, setLastParams] = useState<UserListParams>({});

  const loadUsers = useCallback(async (params: UserListParams = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminApi.listUsers(params);
      setUsers(response.users);
      setTotalCount(response.total_count);
      setHasMore(response.has_more);
      setLastParams(params);
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    await loadUsers(lastParams);
  }, [lastParams]);

  const searchUsers = useCallback(async (query: string) => {
    const params = { ...lastParams, search: query, offset: 0 };
    await loadUsers(params);
  }, [lastParams]);

  return {
    users,
    loading,
    error,
    totalCount,
    hasMore,
    loadUsers,
    refreshUsers,
    searchUsers
  };
};

// ================================
// USER CREDITS HOOK
// ================================

interface UseUserCreditsReturn {
  credits: UserCreditsResponse | null;
  loading: boolean;
  error: string | null;
  loadCredits: (userIdentifier: UserIdentifier) => Promise<void>;
  refreshCredits: () => Promise<void>;
}

export const useUserCredits = (): UseUserCreditsReturn => {
  const [credits, setCredits] = useState<UserCreditsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<UserIdentifier | null>(null);

  const loadCredits = useCallback(async (userIdentifier: UserIdentifier) => {
    setLoading(true);
    setError(null);
    setCurrentUser(userIdentifier);
    
    try {
      const response = await adminApi.getUserCredits(userIdentifier);
      setCredits(response);
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to load user credits:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCredits = useCallback(async () => {
    if (currentUser) {
      await loadCredits(currentUser);
    }
  }, [loadCredits, currentUser]);

  return {
    credits,
    loading,
    error,
    loadCredits,
    refreshCredits
  };
};

// ================================
// USER PROJECTS HOOK
// ================================

interface UseUserProjectsReturn {
  projects: UserProjectsResponse | null;
  loading: boolean;
  error: string | null;
  loadProjects: (userIdentifier: UserIdentifier) => Promise<void>;
  refreshProjects: () => Promise<void>;
}

export const useUserProjects = (): UseUserProjectsReturn => {
  const [projects, setProjects] = useState<UserProjectsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<UserIdentifier | null>(null);

  const loadProjects = useCallback(async (userIdentifier: UserIdentifier) => {
    setLoading(true);
    setError(null);
    setCurrentUser(userIdentifier);

    try {
      const response = await adminApi.getUserProjects(userIdentifier);
      setProjects(response);
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to load user projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    if (currentUser) {
      await loadProjects(currentUser);
    }
  }, [loadProjects, currentUser]);

  return {
    projects,
    loading,
    error,
    loadProjects,
    refreshProjects
  };
};

// ================================
// ADMIN OPERATIONS HOOK
// ================================

interface UseAdminOperationsReturn {
  loading: boolean;
  error: string | null;
  addCredits: (userIdentifier: UserIdentifier, amount: number, creditType: string, description?: string) => Promise<boolean>;
  changePlan: (userIdentifier: UserIdentifier, planType: string, _weeklyLimit?: number, monthlyLimit?: number) => Promise<boolean>;
  updateLimits: (userIdentifier: UserIdentifier, _weeklyLimit?: number, monthlyLimit?: number) => Promise<boolean>;
  clearError: () => void;
}

export const useAdminOperations = (): UseAdminOperationsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const addCredits = useCallback(async (
    userIdentifier: UserIdentifier,
    amount: number,
    creditType: string,
    description?: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      await adminApi.addCreditsToUser(userIdentifier, {
        amount,
        credit_type: creditType as any,
        description
      });
      return true;
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to add credits:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const changePlan = useCallback(async (
    userIdentifier: UserIdentifier,
    planType: string,
    _weeklyLimit?: number,  // Deprecated, kept for compatibility
    monthlyLimit?: number
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      await adminApi.changeUserPlan(userIdentifier, {
        plan_type: planType as any,
        monthly_limit: monthlyLimit
      });
      return true;
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to change plan:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLimits = useCallback(async (
    userIdentifier: UserIdentifier,
    _weeklyLimit?: number,  // Deprecated, kept for compatibility
    monthlyLimit?: number
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      await adminApi.updateUserLimits(userIdentifier, {
        monthly_limit: monthlyLimit
      });
      return true;
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to update limits:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    addCredits,
    changePlan,
    updateLimits,
    clearError
  };
};

// ================================
// BILLING CONFIG HOOK
// ================================

interface UseBillingConfigReturn {
  config: any | null;
  loading: boolean;
  error: string | null;
  loadConfig: () => Promise<void>;
}

export const useBillingConfig = (): UseBillingConfigReturn => {
  const [config, setConfig] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminApi.getBillingConfig();
      setConfig(response.config);
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to load billing config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    config,
    loading,
    error,
    loadConfig
  };
};

// ================================
// COMBINED ADMIN DATA HOOK
// ================================

interface UseAdminDataReturn {
  users: UseUserListReturn;
  credits: UseUserCreditsReturn;
  projects: UseUserProjectsReturn;
  operations: UseAdminOperationsReturn;
  billingConfig: UseBillingConfigReturn;
}

/**
 * Combined hook that provides all admin data management functionality
 */
export const useAdminData = (): UseAdminDataReturn => {
  const users = useUserList();
  const credits = useUserCredits();
  const projects = useUserProjects();
  const operations = useAdminOperations();
  const billingConfig = useBillingConfig();

  return {
    users,
    credits,
    projects,
    operations,
    billingConfig
  };
};

export default useAdminData;

